import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Run an async loader whenever `deps` change and expose its envelope.
 *   const { status, value, count, error, reload } = useAsync(() => getProduct(slug), [slug]);
 * status: 'loading' | 'ready' | 'error'. Stale responses (deps changed while
 * a request was in flight) are ignored. Keeps the previous value while a new
 * request runs when `keepPrevious` is set (for "load more" and filters).
 */
export default function useAsync(loader, deps, { keepPrevious = false } = {}) {
  const [state, setState] = useState({ status: 'loading', value: undefined, count: undefined, error: null });
  const [attempt, setAttempt] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let active = true;
    setState((s) =>
      keepPrevious && s.status === 'ready'
        ? { ...s, status: 'refreshing' }
        : { status: 'loading', value: undefined, count: undefined, error: null }
    );
    Promise.resolve()
      .then(() => loaderRef.current())
      .then((res) => {
        if (active) setState({ status: 'ready', value: res && res.value, count: res && res.count, error: null });
      })
      .catch((error) => {
        if (active && error.code !== 'REQUEST_CANCELED') {
          setState({ status: 'error', value: undefined, count: undefined, error });
        }
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, reload };
}
